import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (token expired)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {
            refreshToken,
          });
          localStorage.setItem("access_token", data.accessToken);
          error.config.headers.Authorization = `Bearer ${data.accessToken}`;
          return axios(error.config);
        } catch {
          // Remove only session/cache keys — a blanket clear() used to wipe
          // eBay credentials and description templates stored alongside them.
          [
            "access_token",
            "refresh_token",
            "user",
            "products",
            "containers",
            "locations",
            "counts",
            "trash",
          ].forEach((k) => localStorage.removeItem(k));
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  },
);

export const auth = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  logout: (refreshToken) => api.post("/auth/logout", { refreshToken }),
  me: () => api.get("/auth/me"),
  updateEmail: (newEmail) => api.patch("/auth/email", { newEmail }),
  updatePassword: (newPassword) => api.patch("/auth/password", { newPassword }),
};

export const entities = {
  list: (params) => api.get("/entities", { params }),
  stats: (entityType) =>
    api.get("/entities/stats", { params: { entity_type: entityType } }),
  dashboard: () =>
    api.get("/entities/dashboard", {
      params: { tz_offset: new Date().getTimezoneOffset() },
    }),
  activity: (limit = 20) =>
    api.get("/entities/activity", { params: { limit } }),
  get: (id) => api.get(`/entities/${id}`),
  board: (pipeline, limit) =>
    api.get("/entities/board", { params: { pipeline, limit } }),
  create: (data) => api.post("/entities", data),
  update: (id, data) => api.patch(`/entities/${id}`, data),
  delete: (id) => api.delete(`/entities/${id}`),
  move: (id, parent_id) => api.post(`/entities/${id}/move`, { parent_id }),
  search: (q, type) => api.get("/entities/search", { params: { q, type } }),
  // Trash operations
  listTrash: (params) => api.get("/entities/trash", { params }),
  trashStats: () => api.get("/entities/trash/stats"),
  restore: (id, clearQrCode = false) =>
    api.post(`/entities/${id}/restore`, { clearQrCode }),
  permanentDelete: (id) => api.delete(`/entities/${id}/permanent`),
  emptyTrash: () => api.delete("/entities/trash"),
  // Tags
  setTags: (id, tagIds) => api.put(`/entities/${id}/tags`, { tag_ids: tagIds }),
  // QR code
  assignQrCode: (id, code) => api.post(`/entities/${id}/qr-code`, { code }),
  // Merge
  merge: (primaryId, otherIds) =>
    api.post(`/entities/${primaryId}/merge`, { entity_ids: otherIds }),
  // Bulk import
  bulkImport: (items) => api.post("/entities/bulk-import", { items }),
};

export const qr = {
  resolve: (code) => api.get(`/qr/resolve/${code}`),
};

export const sales = {
  list: (params) => api.get("/sales", { params }),
  create: (data) => api.post("/sales", data),
  stats: () => api.get("/sales/stats"),
  update: (id, data) => api.patch(`/sales/${id}`, data),
  delete: (id) => api.delete(`/sales/${id}`),
};

export const settings = {
  get: () => api.get("/settings"),
  update: (data) => api.patch("/settings", data),
};

export const views = {
  list: () => api.get("/views"),
  get: (id) => api.get(`/views/${id}`),
  create: (data) => api.post("/views", data),
  update: (id, data) => api.patch(`/views/${id}`, data),
  updateSlot: (id, position, entityId) =>
    api.patch(`/views/${id}/slot/${position}`, { entity_id: entityId }),
  delete: (id) => api.delete(`/views/${id}`),
};

export const data = {
  // Blob download through the authenticated client — the API only reads the
  // Authorization header, so a plain ?token= link would 401.
  export: () => api.get("/data/export", { responseType: "blob" }),
  import: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/data/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

export const tags = {
  list: () => api.get("/tags"),
  create: (data) => api.post("/tags", data),
  update: (id, data) => api.patch(`/tags/${id}`, data),
  delete: (id) => api.delete(`/tags/${id}`),
};

export const media = {
  downloadFromUrl: (url, entityId) =>
    api.post("/media/download-from-url", { url, entityId }),
  // Resolve an Amazon product image from an ASIN and attach it to the product.
  fromAsin: (asin, entityId, force = false) =>
    api.post("/media/from-asin", { asin, entityId, force }),
  // Products with an ASIN but no photo.
  missingImages: () => api.get("/media/missing-images"),
  getImageUrl: (filename) =>
    filename?.startsWith("http")
      ? filename
      : `${API_URL}/api/media/file/${encodeURIComponent(filename)}`,
  cache: (mediaId, externalUrl) =>
    api.post("/media/cache", { mediaId, externalUrl }),
  // List all media for an entity
  list: (entityId) => api.get(`/media/${entityId}`),
  // Add media from URL
  addFromUrl: (entityId, url, isPrimary = false) =>
    api.post(`/media/${entityId}/from-url`, { url, is_primary: isPrimary }),
  // Upload file
  upload: (entityId, file, isPrimary = true) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("is_primary", isPrimary);
    return api.post(`/media/${entityId}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  // Set as primary
  setPrimary: (mediaId) => api.patch(`/media/${mediaId}/primary`),
  // Delete media
  delete: (mediaId) => api.delete(`/media/${mediaId}`),
};

export const email = {
  getAuthUrl: () => api.get("/email/auth-url"),
  callback: (code) => api.post("/email/callback", { code }),
  getStatus: () => api.get("/email/status"),
  disconnect: () => api.post("/email/disconnect"),
  scan: (history = "30d") => api.post("/email/scan", { history }),
  getPending: () => api.get("/email/pending"),
  addProducts: (productIds, parentId) =>
    api.post("/email/add", { productIds, parentId }),
  dismissProducts: (productIds) => api.post("/email/dismiss", { productIds }),
  restoreProduct: (productId) => api.post(`/entities/${productId}/restore`),
};

export const marketplace = {
  // List all marketplace listings
  list: (params) => api.get("/marketplace", { params }),
  // Get marketplace stats
  stats: () => api.get("/marketplace/stats"),
  // Get all listings for an entity
  getByEntity: (entityId) => api.get(`/marketplace/entity/${entityId}`),
  // Get specific listing by entity and marketplace
  getByEntityAndMarketplace: (entityId, marketplaceName) =>
    api.get(`/marketplace/entity/${entityId}/${marketplaceName}`),
  // Get a specific listing
  get: (id) => api.get(`/marketplace/${id}`),
  // Create a new listing
  create: (data) => api.post("/marketplace", data),
  // Update a listing
  update: (id, data) => api.patch(`/marketplace/${id}`, data),
  // Delete a listing
  delete: (id) => api.delete(`/marketplace/${id}`),
};

// Server-credentialed eBay operations (workers/src/routes/ebayAccount.js).
// The web still lists through its own localStorage credentials; these sync
// them up so the phone app and the order-sync cron can act too.
export const ebayAccount = {
  syncCredentials: (data) => api.put("/ebay-account/credentials", data),
  status: () => api.get("/ebay-account/status"),
  syncOrders: () => api.post("/ebay-account/sync-orders"),
  lookup: (params) => api.get("/ebay-account/lookup", { params }),
};

export default api;
